import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';
import ImageUpload from '../../components/ImageUpload';
import { settingsAPI, authAPI } from '../../services/api';
import { Settings as SettingsIcon, Clock, MapPin, Image, Lock, Save } from 'lucide-react';

const Settings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Logo state
  const [logoUrl, setLogoUrl] = useState('');
  
  // Working hours state
  const [workingHours, setWorkingHours] = useState([
    { day: 'Monday', open: '09:00', close: '18:00', is_closed: false },
    { day: 'Tuesday', open: '09:00', close: '18:00', is_closed: false },
    { day: 'Wednesday', open: '09:00', close: '18:00', is_closed: false },
    { day: 'Thursday', open: '09:00', close: '18:00', is_closed: false },
    { day: 'Friday', open: '09:00', close: '18:00', is_closed: false },
    { day: 'Saturday', open: '10:00', close: '16:00', is_closed: false },
    { day: 'Sunday', open: '10:00', close: '16:00', is_closed: true }
  ]);
  
  // Location state
  const [location, setLocation] = useState({
    address: '',
    city: '',
    country: 'United Arab Emirates',
    map_url: ''
  });
  
  // Password state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      const data = response.data;
      
      if (data.logo_url) setLogoUrl(data.logo_url);
      if (data.working_hours && data.working_hours.length > 0) {
        setWorkingHours(data.working_hours);
      }
      if (data.location && data.location.address) {
        setLocation(data.location);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (section) => {
    setSaving(true);
    try {
      const updateData = {};
      
      if (section === 'logo' || section === 'all') {
        updateData.logo_url = logoUrl;
      }
      if (section === 'hours' || section === 'all') {
        updateData.working_hours = workingHours;
      }
      if (section === 'location' || section === 'all') {
        updateData.location = location;
      }
      
      await settingsAPI.update(updateData);
      toast({ title: 'Success', description: 'Settings saved successfully' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleWorkingHoursChange = (index, field, value) => {
    const updated = [...workingHours];
    updated[index] = { ...updated[index], [field]: value };
    setWorkingHours(updated);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast({
        title: 'Error',
        description: 'New passwords do not match',
        variant: 'destructive'
      });
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters',
        variant: 'destructive'
      });
      return;
    }
    
    setChangingPassword(true);
    try {
      await authAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      toast({ title: 'Success', description: 'Password changed successfully' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to change password',
        variant: 'destructive'
      });
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
          <SettingsIcon className="text-amber-600" size={24} />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Manage your website settings</p>
        </div>
      </div>

      <Tabs defaultValue="logo" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="logo" className="flex items-center gap-2">
            <Image size={16} />
            Logo
          </TabsTrigger>
          <TabsTrigger value="hours" className="flex items-center gap-2">
            <Clock size={16} />
            Working Hours
          </TabsTrigger>
          <TabsTrigger value="location" className="flex items-center gap-2">
            <MapPin size={16} />
            Location
          </TabsTrigger>
          <TabsTrigger value="password" className="flex items-center gap-2">
            <Lock size={16} />
            Password
          </TabsTrigger>
        </TabsList>

        {/* Logo Tab */}
        <TabsContent value="logo">
          <Card>
            <CardHeader>
              <CardTitle>Company Logo</CardTitle>
              <CardDescription>Upload your company logo to display on the website</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ImageUpload
                value={logoUrl}
                onChange={setLogoUrl}
              />
              <Button
                onClick={() => handleSaveSettings('logo')}
                disabled={saving}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Save size={16} className="mr-2" />
                {saving ? 'Saving...' : 'Save Logo'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Working Hours Tab */}
        <TabsContent value="hours">
          <Card>
            <CardHeader>
              <CardTitle>Working Hours</CardTitle>
              <CardDescription>Set your business operating hours</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {workingHours.map((wh, index) => (
                <div key={wh.day} className="flex items-center gap-4 py-2 border-b last:border-0">
                  <div className="w-28 font-medium">{wh.day}</div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={wh.is_closed}
                      onChange={(e) => handleWorkingHoursChange(index, 'is_closed', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-600">Closed</span>
                  </label>
                  {!wh.is_closed && (
                    <>
                      <Input
                        type="time"
                        value={wh.open}
                        onChange={(e) => handleWorkingHoursChange(index, 'open', e.target.value)}
                        className="w-32"
                      />
                      <span className="text-gray-500">to</span>
                      <Input
                        type="time"
                        value={wh.close}
                        onChange={(e) => handleWorkingHoursChange(index, 'close', e.target.value)}
                        className="w-32"
                      />
                    </>
                  )}
                </div>
              ))}
              <Button
                onClick={() => handleSaveSettings('hours')}
                disabled={saving}
                className="bg-amber-600 hover:bg-amber-700 mt-4"
              >
                <Save size={16} className="mr-2" />
                {saving ? 'Saving...' : 'Save Working Hours'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Location Tab */}
        <TabsContent value="location">
          <Card>
            <CardHeader>
              <CardTitle>Business Location</CardTitle>
              <CardDescription>Set your business address and location details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Street Address</Label>
                <Input
                  value={location.address}
                  onChange={(e) => setLocation({ ...location, address: e.target.value })}
                  placeholder="123 Business Street"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={location.city}
                    onChange={(e) => setLocation({ ...location, city: e.target.value })}
                    placeholder="Dubai"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Input
                    value={location.country}
                    onChange={(e) => setLocation({ ...location, country: e.target.value })}
                    placeholder="United Arab Emirates"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Google Maps URL (optional)</Label>
                <Input
                  value={location.map_url || ''}
                  onChange={(e) => setLocation({ ...location, map_url: e.target.value })}
                  placeholder="https://maps.google.com/..."
                />
              </div>
              <Button
                onClick={() => handleSaveSettings('location')}
                disabled={saving}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Save size={16} className="mr-2" />
                {saving ? 'Saving...' : 'Save Location'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Password Tab */}
        <TabsContent value="password">
          <Card>
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
              <CardDescription>Update your admin account password</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label>Current Password</Label>
                  <Input
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={changingPassword}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  <Lock size={16} className="mr-2" />
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
